import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, LogOut, User, ChevronDown, Crown, Settings,
  Sun, Moon, LayoutDashboard
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

export function Header() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  return (
    <header className="border-b border-border bg-card/50 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-indigo-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-foreground">PostedIn</span>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-muted hover:text-foreground transition-colors"
              data-testid="header-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {user?.subscription === 'pro' && (
              <span className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 text-amber-500 text-xs font-medium">
                <Crown className="w-3 h-3" />
                Pro
              </span>
            )}
            
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center gap-2"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-card-hover flex items-center justify-center">
                    <User className="w-4 h-4 text-muted" />
                  </div>
                )}
                <ChevronDown className="w-4 h-4 text-muted" />
              </button>
              
              <AnimatePresence>
                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50"
                    >
                      <div className="p-3 border-b border-border">
                        <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                        <p className="text-xs text-muted truncate">{user?.email}</p>
                      </div>
                      <div className="p-1">
                        <button
                          onClick={() => { navigate('/dashboard'); setShowMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-card-hover rounded-lg transition-colors"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          Dashboard
                        </button>
                        <button
                          onClick={() => { navigate('/profile'); setShowMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-card-hover rounded-lg transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          Profile
                        </button>
                        <button
                          onClick={() => { navigate('/subscription'); setShowMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-card-hover rounded-lg transition-colors"
                        >
                          <Crown className="w-4 h-4" />
                          Subscription
                        </button>
                        <button
                          onClick={() => { logout(); setShowMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-card-hover rounded-lg transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Log out
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
